import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { X, ChevronRight, ChevronLeft, Sparkles, Rocket, Check } from 'lucide-react';

const tutorialSteps = [
  {
    id: 'welcome',
    title: 'Bem-vindo ao ViajeJunto! 🎉',
    description: 'Vamos fazer um tour rápido para você conhecer todas as funcionalidades da plataforma.',
    image: '✈️',
  },
  {
    id: 'create_trip',
    title: 'Crie sua primeira viagem',
    description: 'Clique em "Nova viagem" para começar a planejar. Defina nome, destino, datas e orçamento.',
    image: '🗺️',
    highlight: '[data-testid="create-trip-btn"]',
  },
  {
    id: 'invite_friends',
    title: 'Convide seus amigos',
    description: 'Cada viagem tem um código único. Compartilhe com os amigos para eles entrarem!',
    image: '👥',
  },
  {
    id: 'add_itinerary',
    title: 'Monte o roteiro',
    description: 'Adicione atividades para cada dia. Inclua horários, locais e custos estimados.',
    image: '📅',
  },
  {
    id: 'manage_payments',
    title: 'Gerencie pagamentos',
    description: 'Crie despesas e o sistema divide automaticamente entre os participantes. Chega de planilhas!',
    image: '💰',
  },
  {
    id: 'create_votes',
    title: 'Crie votações',
    description: 'Não sabe o que fazer? Crie uma votação e deixe o grupo decidir democraticamente!',
    image: '🗳️',
  },
  {
    id: 'organize_carpools',
    title: 'Organize caronas',
    description: 'Ofereça ou peça carona. O sistema calcula custos de combustível e pedágios.',
    image: '🚗',
  },
  {
    id: 'completed',
    title: 'Tudo pronto! 🚀',
    description: 'Agora você conhece o ViajeJunto! Explore à vontade e tenha uma ótima viagem!',
    image: '🎊',
  },
];

export const TutorialOverlay = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { api } = useAuth();
  
  const step = tutorialSteps[currentStep];
  const isLastStep = currentStep === tutorialSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = async () => {
    if (isLastStep) {
      try {
        await api.post(`/tutorial/complete-step/${step.id}`);
      } catch (e) {
        console.log('Error completing step:', e);
      }
      onComplete?.();
    } else {
      try {
        await api.post(`/tutorial/complete-step/${step.id}`);
      } catch (e) {
        console.log('Error completing step:', e);
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    try {
      await api.post('/tutorial/skip');
    } catch (e) {
      console.log('Error skipping tutorial:', e);
    }
    onClose?.();
  };

  // Highlight element effect
  useEffect(() => {
    if (step.highlight) {
      const element = document.querySelector(step.highlight);
      if (element) {
        element.classList.add('tutorial-highlight');
        return () => element.classList.remove('tutorial-highlight');
      }
    }
  }, [step]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-0 shadow-float overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-pink-500 p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  <span className="text-sm font-medium">Tutorial</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20 h-8 w-8"
                  onClick={handleSkip}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Progress dots */}
              <div className="flex justify-center gap-1.5 mb-4">
                {tutorialSteps.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentStep
                        ? 'bg-white'
                        : index < currentStep
                        ? 'bg-white/60'
                        : 'bg-white/30'
                    }`}
                  />
                ))}
              </div>

              {/* Icon */}
              <div className="text-6xl text-center mb-4">{step.image}</div>
            </div>

            <CardContent className="p-6">
              {/* Content */}
              <div className="text-center mb-6">
                <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400">{step.description}</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                {!isFirstStep && (
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    className="rounded-full"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Voltar
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600"
                  data-testid="tutorial-next-btn"
                >
                  {isLastStep ? (
                    <>
                      <Rocket className="w-4 h-4 mr-2" />
                      Começar!
                    </>
                  ) : (
                    <>
                      Próximo
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>

              {/* Skip link */}
              {!isLastStep && (
                <button
                  onClick={handleSkip}
                  className="w-full mt-4 text-sm text-slate-400 hover:text-slate-600 transition-colors"
                >
                  Pular tutorial
                </button>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export const useTutorial = () => {
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialProgress, setTutorialProgress] = useState(null);
  const { api, isAuthenticated } = useAuth();

  const checkTutorialStatus = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await api.get('/tutorial/progress');
      setTutorialProgress(response.data);
      
      // Show tutorial if not completed and not skipped
      if (!response.data.is_completed && !response.data.skipped) {
        setShowTutorial(true);
      }
    } catch (error) {
      console.log('Error fetching tutorial progress:', error);
    }
  }, [api, isAuthenticated]);

  const startTutorial = async () => {
    try {
      await api.post('/tutorial/restart');
      setShowTutorial(true);
    } catch (error) {
      console.log('Error restarting tutorial:', error);
    }
  };

  const closeTutorial = () => {
    setShowTutorial(false);
  };

  const completeTutorial = () => {
    setShowTutorial(false);
    setTutorialProgress({ ...tutorialProgress, is_completed: true });
  };

  return {
    showTutorial,
    tutorialProgress,
    checkTutorialStatus,
    startTutorial,
    closeTutorial,
    completeTutorial,
  };
};

export default TutorialOverlay;
